import { useState } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useStripeCheckout, type ProductType } from "../hooks/useStripeCheckout";
import toast from "react-hot-toast";

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "pk_test_placeholder"
);

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  productType: ProductType;
  productId: string;
  productName: string;
  productPrice: number;
  productCurrency: string;
  productImage?: string;
  onSuccess: () => void;
}

function PaymentForm({
  productType,
  productId,
  productName,
  productPrice,
  productCurrency,
  onSuccess,
  onClose,
}: Omit<PaymentModalProps, "isOpen" | "productImage">) {
  const stripe = useStripe();
  const elements = useElements();
  const { createPaymentIntent, confirmCardPayment, showSuccess, loading } = useStripeCheckout();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      toast.error("Stripe yüklenemedi. Lütfen sayfayı yenileyin.");
      return;
    }

    if (loading || processing) {
      return;
    }

    setProcessing(true);

    try {
      // 1. Backend'e payment intent oluştur
      const intentResponse = await createPaymentIntent(
        productType,
        productId,
        productCurrency.toUpperCase()
      );

      if (!intentResponse?.data?.clientSecret) {
        setProcessing(false);
        return;
      }

      const { clientSecret } = intentResponse.data;

      // 2. Stripe ile ödemeyi tamamla
      const result = await confirmCardPayment(stripe, elements, clientSecret);

      if (result.success) {
        showSuccess("Ödeme başarılı! Premium içeriğiniz hesabınıza eklendi. 🎉");
        onSuccess();
        setTimeout(() => {
          onClose();
        }, 2000);
      }

      setProcessing(false);
    } catch (err: any) {
      toast.error(err.message || "Ödeme sırasında bir hata oluştu");
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Ürün Bilgisi */}
      <div
        style={{
          padding: 16,
          background: "var(--card-bg, #1a1a1a)",
          borderRadius: 8,
          border: "1px solid var(--muted, #333)",
        }}
      >
        <h3 style={{ margin: 0, marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
          {productName}
        </h3>
        <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "var(--primary, #FFD700)" }}>
          {productPrice} {productCurrency.toUpperCase()}
        </p>
      </div>

      {/* Kart Bilgileri */}
      <div
        style={{
          padding: 16,
          background: "var(--card-bg, #1a1a1a)",
          borderRadius: 8,
          border: "1px solid var(--muted, #333)",
        }}
      >
        <label
          style={{
            display: "block",
            marginBottom: 8,
            fontSize: 14,
            fontWeight: 500,
            color: "var(--text, #fff)",
          }}
        >
          Kart Bilgileri
        </label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#fff",
                "::placeholder": {
                  color: "#aaa",
                },
                backgroundColor: "#0a0a0a",
              },
              invalid: {
                color: "#ff4444",
              },
            },
            hidePostalCode: false,
          }}
        />
      </div>

      {/* Test Kartı Bilgisi */}
      <div
        style={{
          padding: 12,
          background: "rgba(255, 215, 0, 0.1)",
          border: "1px solid rgba(255, 215, 0, 0.3)",
          borderRadius: 8,
          fontSize: 13,
          color: "var(--text, #fff)",
        }}
      >
        <strong>Test Kartı:</strong> 4242 4242 4242 4242
        <br />
        <span style={{ fontSize: 12, opacity: 0.8 }}>
          Herhangi bir gelecek tarihi ve 3 haneli CVC kullanabilirsiniz.
        </span>
      </div>

      {/* Butonlar */}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onClose}
          disabled={processing}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "1px solid var(--muted, #333)",
            background: "transparent",
            color: "var(--text, #fff)",
            cursor: processing ? "not-allowed" : "pointer",
            fontSize: 14,
            fontWeight: 500,
            opacity: processing ? 0.5 : 1,
          }}
        >
          İptal
        </button>
        <button
          type="submit"
          disabled={!stripe || processing || loading}
          style={{
            padding: "10px 24px",
            borderRadius: 8,
            border: "none",
            background: processing || loading ? "#666" : "var(--primary, #FFD700)",
            color: processing || loading ? "#aaa" : "#000",
            cursor: !stripe || processing || loading ? "not-allowed" : "pointer",
            fontSize: 14,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {processing || loading ? (
            <>
              <span className="spinner" />
              İşleniyor...
            </>
          ) : (
            "Ödemeyi Tamamla"
          )}
        </button>
      </div>
    </form>
  );
}

export default function PaymentModal(props: PaymentModalProps) {
  if (!props.isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={props.onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(4px)",
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 500,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--bg, #0a0a0a)",
          border: "1px solid var(--muted, #333)",
          borderRadius: 12,
          padding: 24,
          zIndex: 9999,
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Ödeme</h2>
          <button
            onClick={props.onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text, #fff)",
              fontSize: 24,
              cursor: "pointer",
              padding: 0,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Kapat"
          >
            ×
          </button>
        </div>

        {/* Ürün Görseli (varsa) */}
        {props.productImage && (
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <img
              src={props.productImage}
              alt={props.productName}
              style={{
                width: "100%",
                maxWidth: 200,
                height: "auto",
                borderRadius: 8,
                border: "1px solid var(--muted, #333)",
              }}
            />
          </div>
        )}

        {/* Payment Form */}
        <Elements stripe={stripePromise}>
          <PaymentForm
            productType={props.productType}
            productId={props.productId}
            productName={props.productName}
            productPrice={props.productPrice}
            productCurrency={props.productCurrency}
            onSuccess={props.onSuccess}
            onClose={props.onClose}
          />
        </Elements>
      </div>

      {/* Spinner CSS */}
      <style>{`
        .spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(0, 0, 0, 0.3);
          border-top-color: #000;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
